import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageBar from '../../../components/PageBar';
import ListGrid from '../../../components/ListGrid';
import PageLoading from '../../../components/PageLoading';
import JobPanel, { StateBadge } from './JobPanel';
import { Figure, Figures } from '../../../components/Figures';
import TodaysReports from './TodaysReports';
import { revealPanel } from '../../../components/ListPanel';
import { useToast } from '../../../components/ToastContext';
import { readTransferOrderFile, importTransferOrders } from './transferOrders';
import { loadQueue, STATES, DOORS, QUEUE_ORDER, URGENCY, tally } from './orderJobs';

/*
  THE JOB QUEUE, 3 October 2026, real from the 4th.

  **Asked for by the owner**, at the start of job 34: *"my visions would be all
  the front doors have their process to standardize and then every jobs gets
  placed in a job queue for me to process."*

  **It reads the real queue from 4 October 2026.** It began as a drawing with
  made-up rows; the jobs below are now whatever has actually arrived.

  **What it is for.** Ordering at MedEx is one process with five front doors: a
  client's PDF purchase order, a client's written email, a rep's email, the
  ClaimSoft transfer order he pulls several times a week, and a fifth, placing
  an order in OnHand, which does not exist yet. Everything that arrives by any
  door is turned into the shape of a ClaimSoft transfer order and waits here
  until he picks it up. **What he does once he picks one up is deliberately not
  designed yet**, his own instruction, so that it does not get decided by
  accident while the queue is being built.

  **The three things this mock-up is really asking him**, each one visible on
  screen rather than written in a list:

    1. Are these the right four states? Ready, Needs you, Reading, Could not
       read are mine, not his.
    2. Is a job the right unit? One transfer order with 28 lines is one job
       here, and so is a rep's email asking for one item.
    3. Is this what he needs to see to choose what to pick up first?
*/

const FILTERS = [
  { key: 'all', label: 'Everything' },
  { key: 'needs_you', label: 'Needs you' },
  { key: 'ready', label: 'Ready' },
  { key: 'working', label: 'Being worked' },
  { key: 'reading', label: 'Being read' },
  { key: 'could_not_read', label: 'Could not read' },
  /* Not a state of the queue but a separate read: jobs he took out of it, kept
     so they can be seen and put back. His answer of 5 October 2026. */
  { key: 'cancelled', label: 'Cancelled' },
  /* Also out of the queue: jobs finished through to ClaimSoft (stage 6). */
  { key: 'done', label: 'Finished' },
];

function when(iso) {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' minute' + (mins === 1 ? '' : 's') + ' ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + ' hour' + (hrs === 1 ? '' : 's') + ' ago';
  const days = Math.round(hrs / 24);
  return days + ' day' + (days === 1 ? '' : 's') + ' ago';
}

/* The strip of numbers above the list, OnHand's own number strip (as the
   movement and run screens use) from 12 October 2026, when he found the page
   cramped and unlike the rest of OnHand. Worked out from the rows rather than
   written down, so it can never disagree with what is under it. The number that
   needs acting on is the one in colour, and only when it is not zero. */
function Counts({ t }) {
  return (
    <Figures>
      <Figure n={t.needs_you} label="Needs you" tone={t.needs_you ? 'var(--accent-ink)' : undefined} />
      <Figure n={t.ready} label="Ready" tone={t.ready ? 'var(--success-ink)' : undefined} />
      <Figure n={t.working} label="Being worked" />
      <Figure n={t.lines} label="Lines in all" />
    </Figures>
  );
}

export default function JobQueuePage() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState(null);
  const [lineCounts, setLineCounts] = useState({});
  const [failed, setFailed] = useState(null);
  const [openId, setOpenId] = useState(null);
  const view = filter === 'cancelled' ? 'cancelled' : filter === 'done' ? 'done' : 'open';
  const toast = useToast();
  const fileRef = useRef(null);
  const [adding, setAdding] = useState(false);
  const panelRef = useRef(null);
  const [added, setAdded] = useState(null);
  /* Bumped when a morning report loads, so an open job reads the new stock. */
  const [reportsVersion, setReportsVersion] = useState(0);

  const load = useCallback(async () => {
    const { jobs: j, lines, error } = await loadQueue(view);
    /* A read that fails is not an empty queue. Drawing one as the other is this
       project's oldest fault, and on this screen it would read as "no work
       today". */
    if (error) { setFailed(error.message); setJobs([]); return; }
    setFailed(null);
    setJobs(j);
    setLineCounts(lines);
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const t = useMemo(() => tally(jobs || [], lineCounts), [jobs, lineCounts]);

  const rows = useMemo(() => {
    if (!jobs) return [];
    const q = search.trim().toLowerCase();
    return jobs
      .filter(j => (filter === 'all' || filter === 'cancelled' || filter === 'done' || j.state === filter))
      .filter(j => !q
        || (j.reference || '').toLowerCase().includes(q)
        || (j.requested_by_name || '').toLowerCase().includes(q)
        || (j.source_reference || '').toLowerCase().includes(q)
        || (j.destination ? j.destination.onhand_name : '').toLowerCase().includes(q)
        || DOORS[j.door].toLowerCase().includes(q))
      .sort((a, b) => (view !== 'open' ? 0 : QUEUE_ORDER.indexOf(a.state) - QUEUE_ORDER.indexOf(b.state)));
  }, [jobs, filter, search, view]);

  /*
    **A ClaimSoft transfer order comes in**, ordering stage 1, 11 October 2026.
    The file is read here, split into one job per transfer order number, and
    each line looked up in the item record. What happened is said on the page,
    one line per transfer order, rather than in a message that disappears.
  */
  const addTransferOrder = async (file) => {
    if (!file) return;
    setAdding(true);
    setAdded(null);
    try {
      const parsed = await readTransferOrderFile(file);
      if (parsed.problem) { setAdded({ file: file.name, problem: parsed.problem }); return; }
      const { results, error } = await importTransferOrders(parsed.orders);
      if (error) { setAdded({ file: file.name, problem: 'OnHand could not read the item record just now: ' + error.message }); return; }
      setAdded({ file: file.name, results, noItem: parsed.noItem, noQuantity: parsed.noQuantity });
      const n = results.filter(r => r.ok).length;
      if (n) toast.success(n === 1 ? 'Transfer order added to the queue' : n + ' transfer orders added to the queue');
      await load();
    } catch (e) {
      setAdded({ file: file.name, problem: 'That file could not be read: ' + (e && e.message ? e.message : String(e)) });
    } finally {
      setAdding(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (jobs === null) return <><PageBar title="Job Queue" /><PageLoading /></>;

  const columns = [
    { key: 'what',  label: 'What arrived', width: 'minmax(240px, 2fr)' },
    { key: 'door',  label: 'Came in by',   width: 'minmax(150px, 1fr)' },
    { key: 'dest',  label: 'Going to',     width: 'minmax(170px, 1.2fr)' },
    { key: 'lines', label: 'Lines',        width: '110px', align: 'right' },
    { key: 'state', label: 'State',        width: '130px' },
    { key: 'when',  label: 'Arrived',      width: '130px', align: 'right' },
  ];

  const cells = j => {
    const c = lineCounts[j.id] || { lines: 0, matched: 0, units: 0 };
    return [
      <span>
        <span className="rpnum">{j.reference}</span>
        {j.urgency !== 'routine' && (
          <span className="oh-pill-soon" style={{ marginLeft: 8 }} title={URGENCY[j.urgency].says}>
            {URGENCY[j.urgency].label}
          </span>
        )}
        <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
          {[j.requested_by_name, j.source_reference, j.customer_po ? 'their PO ' + j.customer_po : null]
            .filter(Boolean).join(' \u00b7 ') || 'No one named'}
        </span>
      </span>,
      DOORS[j.door] || j.door,
      j.destination
        ? j.destination.onhand_name
        : <span style={{ color: 'var(--warning-ink)', fontWeight: 700 }}>not known yet</span>,
      <span>
        {c.lines}
        {c.lines > c.matched && (
          <span style={{ display: 'block', fontSize: 13, color: 'var(--warning-ink)', fontWeight: 700 }}>
            {c.lines - c.matched} unmatched
          </span>
        )}
      </span>,
      <StateBadge state={j.state} />,
      when(j.arrived_at),
    ];
  };

  return (
    <>
      <PageBar
        title="Job Queue"
        subtitle="Everything that has arrived, in one shape, waiting to be worked"
        meta={view === 'cancelled'
          ? <span><b>{jobs.length}</b> cancelled</span>
          : view === 'done'
          ? <span><b>{jobs.length}</b> finished</span>
          : <span><b>{t.needs_you + t.ready + t.working}</b> you can pick up</span>}
        controls={
          <>
            <input className="tb-search" placeholder="Search a job, a place or who asked"
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className="tb-select" value={filter} onChange={e => setFilter(e.target.value)}
              aria-label="Which jobs to show">
              {FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </>
        }
        actions={
          <>
            <input ref={fileRef} type="file" accept=".csv,.txt,text/csv" style={{ display: 'none' }}
              onChange={e => addTransferOrder(e.target.files && e.target.files[0])} />
            <button type="button" className="tb-btn tb-btn-primary" disabled={adding}
              onClick={() => fileRef.current && fileRef.current.click()}>
              {adding ? 'Reading the transfer order...' : '+ Add a transfer order'}
            </button>
          </>
        }
      />

      {added && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <b>{added.file}</b>
            <button type="button" className="btn btn-quiet" onClick={() => setAdded(null)}>Close</button>
          </div>
          {added.problem ? (
            <p style={{ color: 'var(--critical-ink)', margin: '6px 0 0' }}>{added.problem}</p>
          ) : (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {added.results.map(r => (
                <li key={r.toNumber} style={{ margin: '3px 0' }}>
                  {r.ok ? (
                    <span>
                      Transfer order <b>{r.toNumber}</b> is <b>{r.reference}</b>, {r.lines} line{r.lines === 1 ? '' : 's'}.
                      {r.unknown > 0 && <span style={{ color: 'var(--warning-ink)', fontWeight: 700 }}> {r.unknown} not known to OnHand yet, so it needs you.</span>}
                      {r.noAe > 0 && <span style={{ color: 'var(--warning-ink)', fontWeight: 700 }}> {r.noAe} with no Account Edge number, so it needs you.</span>}
                      {!r.destinationKnown && <span style={{ color: 'var(--warning-ink)', fontWeight: 700 }}> Where it is going is not known, so it needs you.</span>}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--critical-ink)' }}>Transfer order <b>{r.toNumber}</b> was not added: {r.why}.</span>
                  )}
                </li>
              ))}
              {added.noQuantity > 0 && <li style={{ color: 'var(--warning-ink)' }}>{added.noQuantity} line{added.noQuantity === 1 ? ' had' : 's had'} no quantity above zero and {added.noQuantity === 1 ? 'was' : 'were'} left out.</li>}
              {added.noItem > 0 && <li style={{ color: 'var(--text-secondary)' }}>{added.noItem} row{added.noItem === 1 ? '' : 's'} with no item number were skipped.</li>}
            </ul>
          )}
        </div>
      )}

      {failed ? (
        <div className="card" style={{ padding: 24 }}>
          <p>OnHand could not read the queue just now.</p>
          <p style={{ color: 'var(--text-secondary)' }}>{failed}</p>
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      ) : (
        <>
          {view === 'open' && <TodaysReports onLoaded={() => setReportsVersion(v => v + 1)} />}
          {view === 'open' && <Counts t={t} />}
          {/*
            **The list on top, the job underneath**, his word of 11 October
            2026: *"split the job queue screen where the top half would be the
            job queue list and when you select a job in the queue the details
            open on the bottom half ... it does not have to be a 50/50 split."*
            With a job open the list keeps about a third of the screen and
            scrolls inside itself, because the job's lines are what he reads.
          */}
          <div style={openId ? { maxHeight: '34vh', overflowY: 'auto', borderRadius: 'var(--oh-radius-card)' } : undefined}>
          <ListGrid
            columns={columns}
            rows={rows}
            keyOf={j => j.id}
            cells={cells}
            rowClass={j => (j.id === openId ? 'is-on' : '')}
            onRowClick={j => { setOpenId(j.id); requestAnimationFrame(() => revealPanel(panelRef.current)); }}
            layoutKey="job_queue"
            layoutTitle="The queue's columns"
            empty={jobs.length === 0 && view === 'cancelled'
              ? 'Nothing has been cancelled.'
              : jobs.length === 0 && view === 'done'
              ? 'Nothing has been finished yet.'
              : jobs.length === 0
              ? 'Nothing is waiting. Orders placed in OnHand, and anything read from an email or a file, land here.'
              : 'No job matches that.'}
          />
          </div>
        </>
      )}

      <div ref={panelRef}>
        {openId && <JobPanel key={openId} jobId={openId} reportsVersion={reportsVersion} onClose={() => setOpenId(null)} onChanged={load} />}
      </div>
    </>
  );
}
